// MesAttestations.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "../MesrequetesStyles/MesAttestations.css";
import "../MesrequetesStyles/MesOrdresShared.css";
import Swal from "sweetalert2";
import useRequestHistoryAutoRefresh from "./useRequestHistoryAutoRefresh";
import { getRequestTracking } from "./requestWorkflowTracking";

import {
    FileText,
    Download,
    Printer,
    Eye,
    Upload,
    X,
    Loader2,
    Plus,
    XCircle,
    Edit,
} from "lucide-react";

import {
    fetchAttestationsRequest,
    getMyAttestationRequestsRequest,
    createAttestationRequestRequest,
    cancelAttestationRequestRequest,
    updateAttestationRequestRequest,
    downloadAttestationPdfRequest,
    downloadAttestationRequestPdfRequest,
    clearAttestationsMessage,
} from "../../../../redux/actions/attestationsActions";

const DEMO_MODE = false;

const mockAttestations = [
    {
        id: 1, titre: "Attestation de Travail", description: "Confirmation de votre emploi actuel",
        couleur: "#f59e0b", downloadMode: "MOTIF", actif: true,
    },
    {
        id: 2, titre: "Attestation de Salaire", description: "Justificatif de salaire pour démarches administratives",
        couleur: "#f59e0b", downloadMode: "MOTIF", actif: true,
    },
    {
        id: 3, titre: "Attestation de Domicile", description: "Confirmation d'adresse",
        couleur: "#10b981", downloadMode: "FREE", actif: true,
    },
];

const mockMyRequests = [
    {
        id: 201, attestationTitre: "Attestation de Travail", status: "PENDING_MANAGER",
        createdAt: "2026-07-28", motif: "VISA", commentaire: "Pour dossier visa Schengen",
        dateLimitePreparation: "2026-08-15", justificatifs: [],
        managerDecision: null, managerName: null, managerComment: null, managerDecisionAt: null,
        adminDecision: null, adminName: null, adminComment: null, adminDecisionAt: null,
    },
    {
        id: 202, attestationTitre: "Attestation de Salaire", status: "APPROVED",
        createdAt: "2026-07-10", motif: "BANQUE", commentaire: "Pour dossier de prêt bancaire",
        dateLimitePreparation: "", justificatifs: ["justificatifs/piece_202.pdf"],
        managerDecision: true, managerName: "Karim Benali", managerComment: "OK", managerDecisionAt: "2026-07-11",
        adminDecision: true, adminName: "Amina Tazi", adminComment: "Validé RH", adminDecisionAt: "2026-07-12",
    },
    {
        id: 203, attestationTitre: "Attestation de Travail", status: "REJECTED",
        createdAt: "2026-06-20", motif: "ADMIN", commentaire: "Démarches administratives",
        dateLimitePreparation: "", justificatifs: [],
        managerDecision: false, managerName: "Karim Benali", managerComment: "Motif insuffisamment détaillé.", managerDecisionAt: "2026-06-21",
        adminDecision: null, adminName: null, adminComment: null, adminDecisionAt: null,
    },
];

const MesAttestations = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const attState = useSelector((state) => state.attestations || {});
    const attestations = DEMO_MODE ? mockAttestations : (attState.attestations || []);
    const myRequests = DEMO_MODE ? mockMyRequests : (attState.myRequests || []);
    const loading = DEMO_MODE ? false : !!attState.loading;
    const apiError = DEMO_MODE ? "" : (attState.error || "");
    const apiMessage = DEMO_MODE ? "" : (attState.message || "");

    const [showDemandeModal, setShowDemandeModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentRequestId, setCurrentRequestId] = useState(null);
    const [showCatalogueModal, setShowCatalogueModal] = useState(false);
    const [catalogueTab, setCatalogueTab] = useState("libre");
    const [selectedAttestation, setSelectedAttestation] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const canCancelRequest = (request) => {
        const status = String(request?.statusRaw || request?.status || "").toUpperCase();
        return (status === "PENDING_MANAGER" || status === "PENDING_ADMIN")
            && request?.managerDecision == null
            && request?.adminDecision == null;
    };

    const handleCancelRequest = (request) => {
        Swal.fire({
            icon: "warning",
            title: "Annuler la demande ?",
            text: "Cette action est possible uniquement avant la première validation du workflow.",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Retour",
        }).then((result) => {
            if (result.isConfirmed) dispatch(cancelAttestationRequestRequest(request.id));
        });
    };
    const [motif, setMotif] = useState("");
    const [commentaire, setCommentaire] = useState("");
    const [dateLimitePreparation, setDateLimitePreparation] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        if (!DEMO_MODE) {
            dispatch(fetchAttestationsRequest());
            dispatch(getMyAttestationRequestsRequest());
        }
    }, [dispatch]);

    useRequestHistoryAutoRefresh(() => {
        if (!DEMO_MODE) dispatch(getMyAttestationRequestsRequest({ silent: true }));
    });

    useEffect(() => {
        if (apiMessage) {
            Swal.fire({
                icon: "success",
                title: "Succès",
                text: String(apiMessage),
                timer: 1800,
                showConfirmButton: false,
            });
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1200);
            return () => clearTimeout(t);
        }
    }, [apiMessage, dispatch]);

    useEffect(() => {
        if (apiError) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: String(apiError),
                timer: 2200,
                showConfirmButton: false,
            });
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1500);
            return () => clearTimeout(t);
        }
    }, [apiError, dispatch]);

    const normalizeMode = (a) => String(a?.downloadMode || a?.mode || "").toUpperCase();
    const normalizeColor = (a, fallback = "#204385") => a?.couleur || a?.color || fallback;

    // ====== Catalogue ======
    const catalogue = useMemo(() => {
        return (attestations || [])
            .map((a) => {
                const mode = normalizeMode(a);
                return {
                    id: a.id,
                    type: a.titre || a.nom || a.name || "Attestation",
                    description: a.description || a.details || "—",
                    couleur: normalizeColor(a, mode === "FREE" ? "#10b981" : "#f59e0b"),
                    telechargerLibre: mode === "FREE",
                    actif: a && a.actif !== false && a.actif !== "false" && a.statut !== "INACTIF" && a.isActif !== false,
                };
            })
            .filter((x) => x.actif);
    }, [attestations]);

    // ====== Historique ======
    const historique = useMemo(() => {
        const toUpper = (v) => String(v || "").toUpperCase();
        return (myRequests || []).map((r) => {
            const status = toUpper(r.status || r.statut);
            const couleur = normalizeColor(r?.attestation, r?.couleur || "#3b82f6");

            return {
                id: r.id,
                attestationId: r.attestation?.id || r.attestationId,
                type: r.attestationTitre || r.attestationNom || r.attestationName || r.attestation?.titre || "Attestation",
                couleur,
                dateDemande: r.createdAt || r.dateDemande || "",
                motif: r.motif || r.commentaire || "",
                commentaire: r.commentaire || "",
                dateLimitePreparation: r.dateLimitePreparation || "",
                justificatifs: r.attachments || r.justificatifs || [],
                statusRaw: status,
                canDownload: status === "APPROVED",
                managerDecision: r.managerDecision,
                managerName: r.managerName,
                managerComment: r.managerComment,
                adminDecision: r.adminDecision,
                adminName: r.adminName,
                adminComment: r.adminComment,
            };
        });
    }, [myRequests]);

    const formatDate = (d) => {
        if (!d) return "—";
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
        return dt.toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" });
    };

    const openMotifModal = (att) => {
        setSelectedAttestation(att);
        setMotif("");
        setCommentaire("");
        setDateLimitePreparation("");
        setAttachments([]);
        setShowDemandeModal(true);
        setShowCatalogueModal(false);
    };

    const handleEditClick = (d) => {
        setSelectedAttestation({ id: d.attestationId, type: d.type });
        setMotif(d.motif || "");
        setCommentaire(d.commentaire || "");
        setDateLimitePreparation(d.dateLimitePreparation ? new Date(d.dateLimitePreparation).toISOString().split('T')[0] : "");
        setAttachments(d.justificatifs || []);
        setIsEditMode(true);
        setCurrentRequestId(d.id);
        setShowDemandeModal(true);
    };

    const resetForm = () => {
        setMotif("");
        setCommentaire("");
        setDateLimitePreparation("");
        setAttachments([]);
        setSelectedAttestation(null);
        setIsEditMode(false);
        setCurrentRequestId(null);
    };

    const submitMotif = (e) => {
        e.preventDefault();
        if (!selectedAttestation?.id) return;

        const payload = {
            attestationId: selectedAttestation.id,
            motif: motif || "",
            commentaire: commentaire || "",
            dateLimitePreparation: dateLimitePreparation || "",
            attachments: attachments || [],
        };

        if (isEditMode && currentRequestId) {
            dispatch(updateAttestationRequestRequest(currentRequestId, payload));
        } else {
            dispatch(createAttestationRequestRequest(payload));
        }

        setShowDemandeModal(false);
        resetForm();
    };

    const downloadFree = async (att, print = false) => {
        if (!att?.id) return;
        setDownloadingId(att.id);
        try {
            dispatch(downloadAttestationPdfRequest({ attestationId: att.id, filename: `${att.type}.pdf`, print }));
        } finally {
            setTimeout(() => setDownloadingId(null), 700);
        }
    };

    const downloadFromRequest = async (h, print = false) => {
        if (!h?.id) return;
        setDownloadingId(h.id);
        try {
            dispatch(downloadAttestationRequestPdfRequest({ requestId: h.id, filename: `${h.type}.pdf`, print }));
        } finally {
            setTimeout(() => setDownloadingId(null), 700);
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files || []);
        setAttachments((p) => [...p, ...files]);
    };

    const removeFile = (index) => {
        setAttachments((p) => p.filter((_, i) => i !== index));
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const getStatutConfig = (statut) => {
        const config = {
            PENDING_MANAGER: { label: t("En attente du manager"), color: "#b45309", bg: "#fef3c7", border: "#fcd34d", dot: "#f59e0b" },
            PENDING_ADMIN: { label: t("En attente RH"), color: "#b45309", bg: "#fef3c7", border: "#fcd34d", dot: "#f59e0b" },
            APPROVED: { label: t("Approuvée"), color: "#166534", bg: "#dcfce7", border: "#86efac", dot: "#16a34a" },
            REJECTED: { label: t("Rejetée"), color: "#991b1b", bg: "#fee2e2", border: "#fca5a5", dot: "#ef4444" },
            CANCELLED: { label: t("Annulée"), color: "#374151", bg: "#f3f4f6", border: "#d1d5db", dot: "#9ca3af" },
        };
        return config[statut] || { label: statut, color: "#374151", bg: "#f3f4f6", border: "#d1d5db", dot: "#9ca3af" };
    };

    const getStatutBadge = (statut) => {
        const c = getStatutConfig(statut);
        return (
            <span style={{
                display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px",
                borderRadius: 999, fontSize: 11, fontWeight: 600, color: c.color,
                background: c.bg, border: `1px solid ${c.border}`, whiteSpace: "nowrap",
            }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
                {c.label}
            </span>
        );
    };

    return (
        <div className="pcPage">
            {/* ── Header ── */}
            <div style={{
                margin: "0 18px 10px 18px", padding: "18px 20px", display: "flex",
                alignItems: "center", justifyContent: "space-between", gap: 20,
                borderBottom: "1px solid #f1f5f9",
            }}>
                <div>
                    <h1 style={{ margin: 0, color: "var(--admin-primary)", fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>
                        {t("Mes attestations")}
                    </h1>
                    <p style={{ margin: "5px 0 0", color: "#94a3b8", fontSize: 12 }}>
                        {t("Soumettez une nouvelle demande et suivez son avancement.")}
                    </p>
                </div>
                <button
                    onClick={() => setShowCatalogueModal(true)}
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px",
                        borderRadius: 9, border: "none", background: "var(--admin-accent)", color: "#fff",
                        fontSize: 13, fontWeight: 600, cursor: "pointer",
                        boxShadow: "0 1px 3px rgba(0,0,0,.12)", transition: "filter .15s",
                        whiteSpace: "nowrap", flexShrink: 0,
                    }}
                    onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.08)"}
                    onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}
                >
                    <Plus size={15} />
                    {t("Nouvelle demande d'attestation")}
                </button>
            </div>

            {/* ── Card Historique ── */}
            <div style={{ padding: "0 18px 28px" }}>
                <div style={{
                    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
                    boxShadow: "0 1px 3px rgba(16,24,40,.05)", overflow: "hidden",
                }}>
                    <div style={{
                        padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--admin-primary)" }}>
                            {t("Historique des demandes")}
                        </h2>
                        {historique.length > 0 && (
                            <span style={{
                                padding: "2px 9px", borderRadius: 999, background: "#f0f4ff",
                                color: "var(--admin-accent)", fontSize: 11, fontWeight: 700, border: "1px solid #dde6fb",
                            }}>
                                {historique.length} {t(historique.length > 1 ? "demandes" : "demande")}
                            </span>
                        )}
                    </div>

                    <div style={{ padding: 14 }}>
                        {loading && historique.length === 0 ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
                                <Loader2 size={28} style={{ color: "var(--admin-accent)", opacity: .7 }} />
                            </div>
                        ) : historique.length === 0 ? (
                            <div style={{
                                padding: "40px 20px", textAlign: "center", display: "flex",
                                flexDirection: "column", alignItems: "center", gap: 10, color: "#94a3b8",
                            }}>
                                <div style={{
                                    width: 48, height: 48, borderRadius: 12, background: "#f8fafc",
                                    border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <FileText size={22} style={{ opacity: .5 }} />
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>{t("Aucune demande enregistrée")}</div>
                            </div>
                        ) : (
                            <div style={{ width: "100%", overflowX: "auto", borderRadius: 10, border: "1px solid #eef1f5" }}>
                                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 920 }}>
                                    <thead>
                                    <tr>
                                        {["Attestation", "Motif", "Date limite", "Date demande", "Statut", "Suivi", ""].map((h, i) => (
                                            <th key={i} style={{
                                                background: "#f8f9fa", color: "#6b7280", fontSize: 11, fontWeight: 700,
                                                textAlign: i === 6 ? "right" : "left", padding: "10px 12px",
                                                borderBottom: "1px solid #e5e7eb", textTransform: "uppercase",
                                                letterSpacing: ".04em", whiteSpace: "nowrap",
                                            }}>{t(h)}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {historique.map((d, idx) => (
                                        <tr key={d.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fcfcfd" }}>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                <div style={{ fontWeight: 600, color: "var(--admin-primary)", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
                                                    <div style={{
                                                        backgroundColor: `${d.couleur}20`, color: d.couleur,
                                                        padding: 6, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center"
                                                    }}>
                                                        <FileText size={14} />
                                                    </div>
                                                    {d.type}
                                                </div>
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#374151" }}>
                                                {d.motif || <span style={{ color: "#d1d5db" }}>—</span>}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                                                {d.dateLimitePreparation ? formatDate(d.dateLimitePreparation) : <span style={{ color: "#d1d5db" }}>—</span>}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                                                {formatDate(d.dateDemande)}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                {getStatutBadge(d.statusRaw)}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#64748b" }}>{getRequestTracking({ ...d, status: d.statusRaw }).label}</td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                                <button
                                                    onClick={() => showDetails(d)}
                                                    title={t("Détails")}
                                                    style={{
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                        width: 32, height: 32, padding: 0, borderRadius: 7,
                                                        border: "1px solid #e2e8f0", background: "#fff", color: "#475569", cursor: "pointer", transition: "all .15s",
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--admin-accent)"; e.currentTarget.style.color = "var(--admin-accent)"; e.currentTarget.style.background = "#f0f4ff"; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#475569"; e.currentTarget.style.background = "#fff"; }}
                                                >
                                                    <Eye size={12} />
                                                </button>
                                                {/*{d.canDownload && (*/}
                                                {/*    <>*/}
                                                {/*        <button*/}
                                                {/*            style={{*/}
                                                {/*                width: 32, height: 32, padding: 0, borderRadius: 7,*/}
                                                {/*                display: "inline-flex", alignItems: "center", justifyContent: "center",*/}
                                                {/*                border: "1px solid #16a34a", color: "#16a34a", background: "#f0fdf4", cursor: "pointer"*/}
                                                {/*            }}*/}
                                                {/*            onClick={(e) => { e.stopPropagation(); downloadFromRequest(d); }}*/}
                                                {/*            title={t("Télécharger")}*/}
                                                {/*        >*/}
                                                {/*            {downloadingId === d.id ? <Loader2 size={12} className="maSpin" /> : <Download size={12} />}*/}
                                                {/*        </button>*/}
                                                {/*        <button*/}
                                                {/*            style={{*/}
                                                {/*                width: 32, height: 32, padding: 0, borderRadius: 7,*/}
                                                {/*                display: "inline-flex", alignItems: "center", justifyContent: "center",*/}
                                                {/*                border: "1px solid #204385", color: "#204385", background: "#eff6ff", cursor: "pointer"*/}
                                                {/*            }}*/}
                                                {/*            onClick={(e) => { e.stopPropagation(); downloadFromRequest(d, true); }}*/}
                                                {/*            title={t("Imprimer")}*/}
                                                {/*        >*/}
                                                {/*            <Printer size={12} />*/}
                                                {/*        </button>*/}
                                                {/*    </>*/}
                                                {/*)}*/}
                                                {canCancelRequest(d) && (
                                                    <>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleEditClick(d); }}
                                                            title={t("Modifier la demande")}
                                                            disabled={loading}
                                                            style={{
                                                                width: 32, height: 32, padding: 0, borderRadius: 7,
                                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                                border: "1px solid #bae6fd", color: "#0284c7", background: "#f0f9ff",
                                                                cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1,
                                                                transition: "all .15s",
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = "#e0f2fe"; e.currentTarget.style.borderColor = "#7dd3fc"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = "#f0f9ff"; e.currentTarget.style.borderColor = "#bae6fd"; }}
                                                        >
                                                            <Edit size={12} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleCancelRequest(d); }}
                                                            title={t("Annuler la demande")}
                                                            disabled={loading}
                                                            style={{
                                                                width: 32, height: 32, padding: 0, borderRadius: 7,
                                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                                border: "1px solid #fca5a5", color: "#dc2626", background: "#fff1f2",
                                                                cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1,
                                                                transition: "all .15s",
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.borderColor = "#ef4444"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = "#fff1f2"; e.currentTarget.style.borderColor = "#fca5a5"; }}
                                                        >
                                                            <XCircle size={12} />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Modal Catalogue ── */}
            {showCatalogueModal && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">{t("Choisir une attestation")}</div>
                                <div className="pcModal__subtitle">{t("Sélectionnez le type de document souhaité")}</div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => { setShowCatalogueModal(false); setCatalogueTab("libre"); }}>
                                <X size={15} />
                            </button>
                        </div>

                        {/* Onglets */}
                        <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", padding: "0 20px" }}>
                            {[
                                { key: "libre", label: `⚡ ${t("Téléchargement libre")}`, color: "#10b981" },
                                { key: "demande", label: `📋 ${t("Sur demande")}`, color: "var(--admin-accent)" },
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => setCatalogueTab(tab.key)}
                                    style={{
                                        padding: "10px 16px",
                                        border: "none",
                                        background: "none",
                                        fontSize: 13,
                                        fontWeight: catalogueTab === tab.key ? 700 : 500,
                                        color: catalogueTab === tab.key ? tab.color : "#94a3b8",
                                        borderBottom: catalogueTab === tab.key ? `2px solid ${tab.color}` : "2px solid transparent",
                                        marginBottom: -1,
                                        cursor: "pointer",
                                        transition: "all .15s",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="pcModal__body" style={{ padding: "20px" }}>
                            {catalogueTab === "libre" && (
                                <>
                                    <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12, marginTop: 0 }}>
                                        {t("Ces attestations sont générées instantanément, sans validation requise.")}
                                    </p>
                                    <div className="moPickerList">
                                        {catalogue.filter(a => a.telechargerLibre).length === 0 ? (
                                            <div style={{ textAlign: "center", padding: "30px 20px", color: "#94a3b8", fontSize: 13 }}>
                                                {t("Aucune attestation disponible en téléchargement libre.")}
                                            </div>
                                        ) : catalogue.filter(a => a.telechargerLibre).map((att) => (
                                            <button
                                                type="button"
                                                key={att.id}
                                                className="moPickerItem"
                                                onClick={() => downloadFree(att)}
                                            >
                                                <span className="moCellIcon" style={{ backgroundColor: `${att.couleur}20`, color: att.couleur }}>
                                                    <FileText size={15} />
                                                </span>
                                                <span>
                                                    <strong>{t(att.type, { defaultValue: att.type })}</strong>
                                                    <small>{t(att.description, { defaultValue: att.description })}</small>
                                                    <span style={{ color: "#10b981", fontSize: 11, fontWeight: 600 }}>⚡ {t("Génération immédiate")}</span>
                                                </span>
                                                <Download size={16} style={{ color: "#10b981", flexShrink: 0 }} />
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}

                            {catalogueTab === "demande" && (
                                <>
                                    <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12, marginTop: 0 }}>
                                        {t("Ces attestations nécessitent une validation par votre manager puis par le service RH.")}
                                    </p>
                                    <div className="moPickerList">
                                        {catalogue.filter(a => !a.telechargerLibre).length === 0 ? (
                                            <div style={{ textAlign: "center", padding: "30px 20px", color: "#94a3b8", fontSize: 13 }}>
                                                {t("Aucune attestation disponible sur demande.")}
                                            </div>
                                        ) : catalogue.filter(a => !a.telechargerLibre).map((att) => (
                                            <button
                                                type="button"
                                                key={att.id}
                                                className="moPickerItem"
                                                onClick={() => openMotifModal(att)}
                                            >
                                                <span className="moCellIcon" style={{ backgroundColor: `${att.couleur}20`, color: att.couleur }}>
                                                    <FileText size={15} />
                                                </span>
                                                <span>
                                                    <strong>{t(att.type, { defaultValue: att.type })}</strong>
                                                    <small>{t(att.description, { defaultValue: att.description })}</small>
                                                    <span style={{ color: "var(--admin-accent)", fontSize: 11, fontWeight: 600 }}>📋 {t("Workflow d'approbation")}</span>
                                                </span>
                                                <Plus size={16} style={{ color: "var(--admin-accent)", flexShrink: 0 }} />
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal Formulaire Demande ── */}
            {showDemandeModal && selectedAttestation && (
                <div className="pcModal">
                    <div className="pcModal__panel">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">{isEditMode ? t("Modifier la demande") : t("Nouvelle demande")}</div>
                                <div className="pcModal__subtitle">{t(selectedAttestation.type, { defaultValue: selectedAttestation.type })}</div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => { setShowDemandeModal(false); resetForm(); }}>
                                <X size={15} />
                            </button>
                        </div>
                        <div className="pcModal__body" style={{ padding: "20px" }}>
                            <form onSubmit={submitMotif}>
                                <div style={{ marginBottom: 15 }}>
                                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>
                                        Motif de la demande <span style={{ color: "red" }}>*</span>
                                    </label>
                                    <select
                                        value={motif}
                                        onChange={(e) => setMotif(e.target.value)}
                                        required
                                        style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: 13 }}
                                    >
                                        <option value="">-- Sélectionner --</option>
                                        <option value="VISA">Dossier Visa</option>
                                        <option value="BANQUE">Dossier Banque</option>
                                        <option value="ADMIN">Démarches administratives</option>
                                        <option value="AUTRE">Autre</option>
                                    </select>
                                </div>

                                <div style={{ marginBottom: 15 }}>
                                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>
                                        Commentaire <span style={{ color: "#9ca3af" }}>(optionnel)</span>
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={commentaire}
                                        onChange={(e) => setCommentaire(e.target.value)}
                                        placeholder="Donnez plus de détails si nécessaire..."
                                        style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: 13, resize: "vertical" }}
                                    />
                                </div>

                                <div style={{ marginBottom: 15 }}>
                                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>
                                        Date limite de préparation <span style={{ color: "#9ca3af" }}>(optionnel)</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={dateLimitePreparation}
                                        onChange={(e) => setDateLimitePreparation(e.target.value)}
                                        min={new Date().toISOString().slice(0, 10)}
                                        style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: 13 }}
                                    />
                                </div>

                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>
                                        Documents joints <span style={{ color: "#9ca3af" }}>(optionnel)</span>
                                    </label>
                                    <div style={{ border: "1px dashed #d1d5db", padding: "15px", textAlign: "center", borderRadius: "6px" }}>
                                        <Upload size={24} style={{ color: "#9ca3af", marginBottom: 6 }} />
                                        <div style={{ fontSize: 12, marginBottom: 8, color: "#6b7280" }}>Glissez vos fichiers ici ou cliquez pour parcourir</div>
                                        <input id="maFileAttestation" type="file" multiple onChange={handleFileUpload} hidden />
                                        <label htmlFor="maFileAttestation" style={{ cursor: "pointer", color: "var(--admin-accent)", fontSize: 12, fontWeight: "bold" }}>
                                            Parcourir
                                        </label>
                                    </div>
                                    {attachments.length > 0 && (
                                        <div style={{ marginTop: 8 }}>
                                            {attachments.map((file, index) => (
                                                <div key={index} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, background: "#f3f4f6", padding: "6px 10px", borderRadius: "4px", marginBottom: 4 }}>
                                                    <span>{file.name || file}</span>
                                                    <button type="button" onClick={() => removeFile(index)} style={{ border: "none", background: "none", cursor: "pointer", color: "#ef4444" }}>
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                                    <button
                                        type="button"
                                        style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13 }}
                                        onClick={() => { setShowDemandeModal(false); resetForm(); }}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading || !motif.trim()}
                                        style={{
                                            padding: "8px 16px", borderRadius: "6px", border: "none",
                                            background: "var(--admin-accent)", color: "#fff", fontSize: 13,
                                            cursor: loading || !motif.trim() ? "not-allowed" : "pointer",
                                            opacity: loading || !motif.trim() ? 0.6 : 1
                                        }}
                                    >
                                        {loading ? "Envoi..." : (isEditMode ? "Enregistrer les modifications" : "Envoyer la demande")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal Détails ── */}
            {showDetailsModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel" style={{ width: "min(660px, 100%)" }}>
                        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--admin-primary)" }}>Détails de la demande</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ padding: "2px 8px", borderRadius: 5, background: "#f0f4ff", color: "var(--admin-accent)", fontSize: 11, fontWeight: 700, border: "1px solid #dde6fb" }}>
                                        {selectedDemande.type}
                                    </span>
                                    {getStatutBadge(selectedDemande.statusRaw)}
                                </div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => setShowDetailsModal(false)}>
                                <X size={15} />
                            </button>
                        </div>

                        <div className="pcModal__body" style={{ padding: "20px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 15, marginBottom: 20 }}>
                                <div>
                                    <div style={{ fontSize: 12, color: "#6b7280" }}>Motif</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedDemande.motif || "—"}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, color: "#6b7280" }}>Date de demande</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{formatDate(selectedDemande.dateDemande)}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, color: "#6b7280" }}>Date limite</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedDemande.dateLimitePreparation ? formatDate(selectedDemande.dateLimitePreparation) : "—"}</div>
                                </div>
                            </div>

                            {selectedDemande.commentaire && (
                                <div style={{ marginBottom: 20 }}>
                                    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Commentaire</div>
                                    <div style={{ fontSize: 13, background: "#f9fafb", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>{selectedDemande.commentaire}</div>
                                </div>
                            )}

                            {selectedDemande.managerDecision !== null && (
                                <div style={{ marginBottom: 16, padding: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Validation manager</div>
                                    <div style={{ fontSize: 13 }}>
                                        <span style={{ color: "#6b7280" }}>Décision :</span>{" "}
                                        <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                            {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                        </b>
                                    </div>
                                    {selectedDemande.managerName && <div style={{ fontSize: 13 }}><span style={{ color: "#6b7280" }}>Par :</span> <b>{selectedDemande.managerName}</b></div>}
                                    {selectedDemande.managerComment && (
                                        <div style={{ fontSize: 13, marginTop: 6, padding: 8, background: "#fee2e2", color: "#991b1b", borderRadius: 4 }}>
                                            {selectedDemande.managerComment}
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedDemande.adminDecision !== null && (
                                <div style={{ marginBottom: 16, padding: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Validation RH</div>
                                    <div style={{ fontSize: 13 }}>
                                        <span style={{ color: "#6b7280" }}>Décision :</span>{" "}
                                        <b style={{ color: selectedDemande.adminDecision ? "#10b981" : "#ef4444" }}>
                                            {selectedDemande.adminDecision ? "Approuvé" : "Rejeté"}
                                        </b>
                                    </div>
                                    {selectedDemande.adminName && <div style={{ fontSize: 13 }}><span style={{ color: "#6b7280" }}>Par :</span> <b>{selectedDemande.adminName}</b></div>}
                                    {selectedDemande.adminComment && (
                                        <div style={{ fontSize: 13, marginTop: 6, padding: 8, background: "#fee2e2", color: "#991b1b", borderRadius: 4 }}>
                                            {selectedDemande.adminComment}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MesAttestations;
